import React, { useMemo, useState } from 'react';
import ReactSelect from "react-select"
import { Box, CircularProgress, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, IconButton, Stack, Typography, Card } from '@mui/material';
import MDButton from 'components/MDButton';
import { useSelector, useDispatch } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import { grey } from "@mui/material/colors";
import { openSnackbar } from '../../../redux/slices/common';
import { closeCustomModalDialog, setModalLoader } from '../../../redux/slices/modalSlice';
import { updateAssignedSubRegion } from "../../../redux/slices/hyperwolf/settings";

export default function UpdateAssignedSubRegion({ closeModal }) {
  const dispatch = useDispatch()
  const { modalData, isLoading = false } = useSelector(state => state.modal);
  const { currentDetails = {}, drivers = [], handleRegionsList, handleDriversList, regionId } = modalData;

  const [details, setDetails] = useState({ ...currentDetails });
  const [errors, setErrors] = useState({});
  const [isDriverDropdownOpen, setIsDriverDropdownOpen] = useState(false);

  const handleOpen = (e) => {
    setIsDriverDropdownOpen(true)
  }

  const handleStatusChange = (tab) => {
    setDetails(prev => ({ ...prev, status: tab }))
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDetails(prev => {
      return { ...prev, [name]: value }
    });
    setIsDriverDropdownOpen(false);
  }

  const showError = (message, errorType) => {
    if (errorType === 'success') {
      dispatch(openSnackbar({ content: message, color: 'success', icon: 'check' }));
    }
    if (errorType === 'error') {
      dispatch(openSnackbar({ content: message, color: 'error', icon: 'warning' }));
    }
  };

  const handleSave = async () => {
    try {
      if (isLoading) return;
      if (!details.driverId) {
        setErrors({ driverId: "Please select driver" })
        return;
      } else {
        setErrors({});
      }

      dispatch(setModalLoader(true));
      const res = await dispatch(updateAssignedSubRegion({ ...details, regionId })).unwrap();
      showError(res?.message, 'success');
      dispatch(closeCustomModalDialog());
      handleRegionsList();
      handleDriversList();
    } catch (err) {
      dispatch(setModalLoader(false));
      showError(err?.message, 'error');
    };
  }

  const avaiableDrivers = useMemo(() => {
    return [...drivers, (currentDetails.driverId ? { _id: currentDetails.driverId, fleetName: currentDetails.driver } : null)]
      ?.filter(Boolean)
      .sort((a, b) => a.fleetName.localeCompare(b.fleetName));
  }, [drivers, currentDetails])

  return (
    <Card sx={{ py: 3, px: 4, borderRadius: 0 }}>
      <DialogTitle id='draggable-dialog-title' sx={{ p: 0, mb: 2 }} display='flex' alignItems='center' justifyContent='space-between'>
        Update Sub Region
        <IconButton onClick={closeModal} edge='end' size='small'>
          <CloseIcon fontSize='medium' />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, mb: 2 }}>
        <DialogContentText>

          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={1.25}>
              <Typography variant='h5' fontWeight="semibold" color="secondary">Status</Typography>
              <Stack direction="row" alignItems="center" gap={.75} lineHeight={1} p={.75} borderRadius={2} bgcolor="#F2F3F7">
                <MDButton
                  sx={{ minWidth: '92px', borderRadius: 1.75, minHeight: '28px', p: .25, fontWeight: 500 }}
                  type='button'
                  variant={details.status ? 'contained' : 'text'}
                  color={details.status ? 'success' : 'dark'}
                  size="small"
                  onClick={() => handleStatusChange(true)}
                >
                  Active
                </MDButton>
                <MDButton
                  sx={{ minWidth: '92px', borderRadius: 1.75, minHeight: '28px', p: .25, fontWeight: 500 }}
                  type='button'
                  variant={!details.status ? 'contained' : 'text'}
                  color={!details.status ? 'error' : 'dark'}
                  size="small"
                  onClick={() => handleStatusChange(false)}
                >
                  Inactive
                </MDButton>
              </Stack>
            </Box>

            <Box className="mt-4 cus-multi-select">
              <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Assigned Driver Today</Typography>
              <ReactSelect
                name="driverId"
                value={avaiableDrivers?.find(driver => driver._id === details?.driverId) || null}
                onChange={selectedOption => handleChange({ target: { name: "driverId", value: selectedOption ? selectedOption._id : "" } })}
                options={avaiableDrivers}
                getOptionLabel={option => option.fleetName}
                getOptionValue={option => option._id}
                placeholder="Select a driver"
                menuIsOpen={isDriverDropdownOpen}
                onMenuOpen={handleOpen}
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: base => ({ ...base, zIndex: 9999 }),
                  control: (provided) => ({ ...provided, minHeight: 40 }),
                }}
                components={{ IndicatorSeparator: null }}
                className='cus-react-select'
                classNamePrefix="form-select"
              />
              {errors?.driverId && <p className="error">{errors?.driverId}</p>}
            </Box>
          </Grid>

        </DialogContentText>
      </DialogContent>

      <DialogActions sx={{ p: 0, my: 2 }} style={{ justifyContent: "start" }}>
        <MDButton
          onClick={handleSave}
          sx={{ minWidth: '160px', height: "44px" }}
          color="primary"
          type='button'
          variant='contained'
        >
          {isLoading ? <CircularProgress size={16} color='light' /> : ' Save'}
        </MDButton>
        <MDButton
          onClick={closeModal}
          sx={{ minWidth: '160px', height: "44px" }}
          type='button'
          variant='outlined'
          color='dark'
        >
          Cancel
        </MDButton>
      </DialogActions>
    </Card>
  );
}